import { Loader2, RefreshCw } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { ApiError, type AdminApi, type SamsAdminEvent, type SamsAuditEventQuery } from "@/api";
import {
  Badge, Banner, Button, Section, SelectField, Table, TableBody, TableCell,
  TableHead, TableHeader, TableRow, TextField,
} from "@/components/ui";
import { ACTION_LABELS, actionLabel, detailEntries, eventTarget, MASKED_TEXT, outcomeLabel } from "@/components/sams-admins/adminRules";
import { compactTime } from "@/utils/format";

type Filters = { area: string; environment: string; target_type: string; target_id: string; operation_id: string };
const EMPTY: Filters = { area: "", environment: "", target_type: "", target_id: "", operation_id: "" };

function queryFor(filters: Filters): SamsAuditEventQuery {
  const area = filters.area.trim();
  return {
    action_prefix: area ? (area.includes(".") || Object.prototype.hasOwnProperty.call(ACTION_LABELS, area) ? area : `${area}.`) : undefined,
    environment: filters.environment === "dev" || filters.environment === "prod" ? filters.environment : undefined,
    target_type: filters.target_type.trim() || undefined,
    target_id: filters.target_id.trim() || undefined,
    operation_id: filters.operation_id.trim() || undefined,
  };
}

function newestFirst(a: SamsAdminEvent, b: SamsAdminEvent) {
  const time = new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  return time || (a.event_id < b.event_id ? 1 : a.event_id > b.event_id ? -1 : 0);
}

/** Group only records actually returned by the server; later pages can complete a group. */
function groupEvents(events: SamsAdminEvent[]) {
  const groups = new Map<string, SamsAdminEvent[]>();
  for (const event of [...events].sort(newestFirst)) {
    const key = event.operation_id ? `operation:${event.operation_id}` : `event:${event.event_id}`;
    const group = groups.get(key);
    if (group) group.push(event);
    else groups.set(key, [event]);
  }
  return [...groups.entries()];
}

// Honor masking before interpreting legacy admin targets too.
function safeTarget(event: SamsAdminEvent) {
  return event.masked ? { text: [event.target_type, MASKED_TEXT].filter(Boolean).join(" "), summary: null } : eventTarget(event);
}

function errorText(error: unknown) {
  if (error instanceof ApiError && error.code) return `${error.message} (${error.code})`;
  return error instanceof Error ? error.message : String(error);
}

export function ChangelogPage({ api }: { api: AdminApi }) {
  const [draft, setDraft] = useState<Filters>(EMPTY);
  const [query, setQuery] = useState<SamsAuditEventQuery>({});
  const [events, setEvents] = useState<SamsAdminEvent[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [reload, setReload] = useState(0);
  const generation = useRef(0);
  const moreInFlight = useRef(false);

  useEffect(() => {
    const current = ++generation.current;
    let active = true;
    setLoading(true);
    setLoadingMore(false);
    moreInFlight.current = false;
    setError("");
    setEvents([]);
    setNextCursor(null);
    void api.listSamsAuditEvents(query).then((page) => {
      if (!active) return;
      setEvents(page.items);
      setNextCursor(page.next_cursor);
    }).catch((err: unknown) => {
      if (active) setError(errorText(err));
    }).finally(() => {
      if (active && current === generation.current) setLoading(false);
    });
    return () => { active = false; };
  }, [api, query, reload]);

  const loadMore = async () => {
    if (!nextCursor || loading || moreInFlight.current) return;
    const current = generation.current;
    moreInFlight.current = true;
    setLoadingMore(true);
    setError("");
    try {
      const page = await api.listSamsAuditEvents({ ...query, cursor: nextCursor });
      if (current !== generation.current) return;
      setEvents((shown) => {
        const unique = new Map(shown.map((event) => [event.event_id, event]));
        for (const event of page.items) unique.set(event.event_id, event);
        return [...unique.values()];
      });
      setNextCursor(page.next_cursor);
    } catch (err) {
      if (current === generation.current) setError(errorText(err));
    } finally {
      if (current === generation.current) {
        moreInFlight.current = false;
        setLoadingMore(false);
      }
    }
  };

  const change = (key: keyof Filters) => (value: string) => setDraft((previous) => ({ ...previous, [key]: value }));
  const groups = groupEvents(events);

  return (
    <div className="grid gap-5">
      <p className="text-[13px] text-muted-foreground">
        SAMS 감사 기록을 최신순으로 표시합니다. 같은 작업의 intent·result는 한 줄로 묶이며, 펼치면 각 기록을 볼 수 있습니다. 감사 기록에 연결되지 않은 변경은 표시되지 않습니다.
      </p>
      <Section title="필터" contentClassName="grid gap-3">
        <form className="grid gap-3" onSubmit={(event) => { event.preventDefault(); setQuery(queryFor(draft)); }}>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
            <TextField label="영역" value={draft.area} onChange={change("area")} placeholder="예: prompt, detection, grant" />
            <SelectField label="환경" value={draft.environment} onChange={change("environment")} options={[
              { value: "", label: "전체" }, { value: "dev", label: "dev" }, { value: "prod", label: "prod" },
            ]} />
            <TextField label="대상 종류" value={draft.target_type} onChange={change("target_type")} placeholder="예: prompt" />
            <TextField label="대상 ID" value={draft.target_id} onChange={change("target_id")} />
            <TextField label="작업 ID (operation_id)" value={draft.operation_id} onChange={change("operation_id")} />
          </div>
          <p className="text-xs text-muted-foreground">영역은 action의 첫 부분으로 검색합니다. 관리자 권한 변경은 seed·grant·revoke·grant_prod·revoke_prod로 검색할 수 있습니다.</p>
          <div className="flex flex-wrap gap-2">
            <Button type="submit" size="sm">필터 적용</Button>
            <Button type="button" variant="outline" size="sm" onClick={() => { setDraft(EMPTY); setQuery({}); }}>초기화</Button>
            <Button type="button" variant="outline" size="sm" disabled={loading} onClick={() => setReload((value) => value + 1)}>
              <RefreshCw />새로고침
            </Button>
          </div>
        </form>
        <p className="text-xs text-muted-foreground">결과·기간 필터는 서버 API에서 지원하지 않아 사용할 수 없습니다.</p>
      </Section>
      {error && <Banner tone="error">{error}</Banner>}
      <Section title="변경 기록" contentClassName="grid gap-3">
        <p className="text-xs text-muted-foreground" role="status">
          {loading ? "기록을 불러오는 중…" : `불러온 기록 ${events.length}건 · 묶음 ${groups.length}개`}
          {nextCursor && " · 이전 기록이 더 있습니다. 더 보기를 누르면 같은 작업의 이전 단계도 합쳐집니다."}
        </p>
        <Table aria-label="SAMS 변경 기록" className="min-w-[800px] table-fixed [&_td]:align-top">
          <TableHeader><TableRow>
            <TableHead className="w-[170px]">최근 시각</TableHead>
            <TableHead className="w-[180px]">동작</TableHead>
            <TableHead>대상·상세</TableHead>
            <TableHead className="w-[130px]">환경·결과</TableHead>
            <TableHead className="w-[180px]">수행자</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {groups.map(([key, group]) => {
              const latest = group[0];
              const result = group.find((event) => event.phase === "result" || event.phase === "reconcile") ?? latest;
              const target = safeTarget(latest);
              const actions = [...new Set(group.map((event) => actionLabel(event.action)))];
              return <TableRow key={key} className="align-top" data-testid={`change-${key}`}>
                <TableCell className="text-xs tabular-nums">{compactTime(latest.created_at)}</TableCell>
                <TableCell className="whitespace-normal break-all">{actions.join(" · ")}</TableCell>
                <TableCell className="whitespace-normal break-all">
                  <p>{target.text}</p>
                  {target.summary && <p className="text-xs text-muted-foreground">{target.summary}</p>}
                  <details className="mt-1 text-xs">
                    <summary className="cursor-pointer text-muted-foreground">상세 · 기록 {group.length}건</summary>
                    {latest.operation_id && <p className="mt-2">작업 ID: {latest.operation_id}</p>}
                    {group.map((event) => <div key={event.event_id} className="mt-2 grid gap-1 border-t pt-2">
                      <p>{compactTime(event.created_at)} · {actionLabel(event.action)} · {event.phase ?? "단계 없음"} · {event.outcome ? outcomeLabel(event.outcome) : "결과 없음"}</p>
                      <p>{event.actor_email} · {safeTarget(event).text}</p>
                      {event.masked ? <p>prod 권한 필요(가려짐)</p> : <>
                        {event.summary && <p>{event.summary}</p>}
                        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                          {detailEntries(event.detail).map(([name, value]) => <div key={name} className="contents">
                            <dt className="text-muted-foreground">{name}</dt><dd className="min-w-0 whitespace-pre-wrap break-all">{value}</dd>
                          </div>)}
                        </dl>
                      </>}
                    </div>)}
                  </details>
                </TableCell>
                <TableCell><span className="flex flex-wrap gap-1">
                  {latest.environment && <Badge size="compact">{latest.environment}</Badge>}
                  {result.outcome && <Badge size="compact">{outcomeLabel(result.outcome)}</Badge>}
                  {result.phase && <Badge size="compact">{result.phase}</Badge>}
                </span></TableCell>
                <TableCell className="whitespace-normal break-all">{latest.actor_email}<p className="text-xs text-muted-foreground">{latest.actor_kind === "system" ? "시스템" : latest.actor_kind === "seed" ? "초기 시드" : "관리자"}</p></TableCell>
              </TableRow>;
            })}
            {!loading && !error && !events.length && <TableRow><TableCell colSpan={5} className="text-muted-foreground">기록이 없습니다.</TableCell></TableRow>}
          </TableBody>
        </Table>
        {nextCursor && <div><Button variant="outline" size="sm" disabled={loading || loadingMore} onClick={() => void loadMore()}>
          {loadingMore && <Loader2 className="animate-spin" />}더 보기
        </Button></div>}
      </Section>
    </div>
  );
}
